import { Row, Col, Divider, Tooltip, Grid } from "antd";
import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";

import { RATypography, RAButtonPrimary, RAButtonSecondary } from "@/components/customs";
import { getAircraftCategories } from "@/stores/apis/apiAircraft";
import { RAImage } from "../customs/Image";
import { SvgSuccessFill, SvgSuccessOutline } from "@/assets/icons";
import { FILE_TYPE } from "@/config/constants";
import { AircraftOwnershipTagTooltip } from "../molecules";
import { sortedCategory } from "@/utils/index";

const { useBreakpoint } = Grid;

export function FormRequestFlightSelectCategory({ data = {}, onChange, onNext, onBack }) {
	const screens = useBreakpoint();
	const { aircraft_categories = [] } = data;
	const [categories, setCategories] = useState([]);
	const [isMobile, setIsMobile] = useState(false);
	const [overlay, setOverlay] = useState(true);

	useEffect(() => {
		if (screens) {
			setIsMobile(!screens?.md);
		}
	}, [screens]);

	const onFetchCategories = async () => {
		let response = await getAircraftCategories();
		if (response && response.success) {
			const c = sortedCategory(response?.data?.docs || []);
			setCategories(c);
		}
	};

	useEffect(() => {
		onFetchCategories();
	}, []);

	const onSelectAircraftCategory = (item) => {
		let newAircraftCategory = [...aircraft_categories];
		let index = newAircraftCategory?.findIndex((v) => v._id === item._id);
		if (index > -1) {
			newAircraftCategory.splice(index, 1);
		} else {
			newAircraftCategory.push(item);
		}
		onChange({ ...data, aircraft_categories: newAircraftCategory });
	};

	const aircraftCategoriesRef = useCallback((node) => {
		if (node) {
			setOverlay(node.offsetHeight + node.scrollTop < node.scrollHeight - 10);
			node.addEventListener("scroll", () => {
				setOverlay(node.offsetHeight + node.scrollTop < node.scrollHeight - 10);
			});
		}
	}, []);

	const _renderAircraftCategories = () => {
		return (
			<AircraftCategories ref={aircraftCategoriesRef} align="middle" gutter={[16, 16]}>
				{categories?.length > 0 ? (
					categories.map((item) => (
						<Col xs={24} key={item._id} style={{ alignSelf: "stretch" }}>
							<CardSectionItem
								screens={screens}
								isMobile={isMobile}
								aircraft={item}
								onSelect={() => onSelectAircraftCategory(item)}
								active={aircraft_categories?.some((v) => v._id === item._id)}
							/>
						</Col>
					))
				) : (
					<RATypography>No Aircraft Categories</RATypography>
				)}
			</AircraftCategories>
		);
	};

	return (
		<ContainerRequestFlight>
			<Row>
				<Col md={24}>
					<WrapperContent>
						<RATypography variant={screens.xs ? "h4" : "h3"}>Select Aircraft Category</RATypography>
					</WrapperContent>
					<RATypography variant="body3" top="12px" color="#595959">
						Customize your flight experience by selecting an aircraft category or move to the next step.
					</RATypography>
				</Col>
			</Row>
			<Divider />
			<div style={{ position: "relative" }}>
				{_renderAircraftCategories()}
				{overlay && <BlurOverlay />}
			</div>
			<Row gutter={[20, 12]} justify="center" align="middle" style={{ marginTop: "30px" }}>
				<ButtonCol xs={12}>
					<RAButtonSecondary title="BACK" onClick={() => onBack()} width="100%" />
				</ButtonCol>
				<ButtonCol xs={12}>
					<RAButtonPrimary
						title="NEXT"
						onClick={() => onNext()}
						width="100%"
						data-testid="flightInfo_nextBtn"
					/>
				</ButtonCol>
			</Row>
		</ContainerRequestFlight>
	);
}

const CardSectionItem = ({ screens, isMobile = false, active = false, aircraft = {}, onSelect }) => {
	const {
		name = "",
		description = "",
		max_fly_times_hours = 0,
		min_fly_times_hours,
		max_passengers = 1,
		images = [],
		is_owner = false,
		is_partner = false,
	} = aircraft;

	const getMainImages = () => images?.find((v) => v.type === FILE_TYPE.MAIN);

	return (
		<WrapperCardSection onClick={onSelect} active={active}>
			<WrapperAircraftOwnershipTag>
				<AircraftOwnershipTagTooltip isOwner={is_owner} isPartner={is_partner} />
				<>{active ? <SvgSuccessFill color="#FFB11A" /> : <SvgSuccessOutline />}</>
			</WrapperAircraftOwnershipTag>
			<WrapperAircraft>
				<WrapperImageAircraft>
					<RAImage
						url={getMainImages()?.url || "/images/example/aircraft_light_turbo.png"}
						width="100%"
						height="100%"
						objectFit="contain"
					/>
				</WrapperImageAircraft>
				<WrapperItem>
					<RATypography variant={`${screens?.sm || screens?.xs ? "h5" : "h4"}`}>{name}</RATypography>
					<Tooltip placement="bottomRight" title={description}>
						<RATypography variant={isMobile ? "body4" : "body3"} top="2px" color="#595959" maxLines={2}>
							{description}
						</RATypography>
					</Tooltip>
					<CustomDivider />
					<AircraftDetailItem>
						<AircraftDetailItemInner>
							<RATypography variant={isMobile ? "body4" : "body3"} color="#595959">
								Range
							</RATypography>
							<RATypography variant={isMobile ? "sub-heading5" : "sub-heading4"}>
								{`${
									min_fly_times_hours > 0 ? `${min_fly_times_hours} - ` : ""
								}${max_fly_times_hours} Hrs`}
							</RATypography>
						</AircraftDetailItemInner>
						<AircraftDetailItemInner>
							<RATypography variant={isMobile ? "body4" : "body3"} color="#595959">
								Passengers
							</RATypography>
							<RATypography variant={isMobile ? "sub-heading5" : "sub-heading4"}>
								{max_passengers ? `${max_passengers} Pax` : "_"}
							</RATypography>
						</AircraftDetailItemInner>
					</AircraftDetailItem>
				</WrapperItem>
			</WrapperAircraft>
		</WrapperCardSection>
	);
};

const AircraftCategories = styled(Row)``;

const BlurOverlay = styled.div`
	position: absolute;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100%;
	height: 100px;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 0) 0%,
		rgba(255, 255, 255, 0) 0%,
		rgba(255, 255, 255, 0.8) 41.09%,
		#fff 100%
	);
`;

const WrapperCardSection = styled(Row)`
	cursor: pointer;
	width: 100%;
	height: 100%;
	background: #ffffff;
	border-radius: 8px;
	border: 1px solid #e9e8e8;
	${({ active }) =>
		active &&
		`
        box-shadow: 0px 4px 40px rgba(76, 85, 94, 0.08);
        border: 1px solid #ffb11a;
    `}
`;

const WrapperAircraftOwnershipTag = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin: 8px;

	@media (max-width: 767px) {
		margin: 12px;
	}
`;

const WrapperAircraft = styled.div`
	display: flex;
	gap: 20px;
	margin: 0 20px 20px 20px;

	@media (max-width: 767px) {
		flex-direction: column;
		gap: 16px;
		margin: 12px;
	}
`;

const WrapperImageAircraft = styled.div`
	width: 200px;
	height: 100%;

	@media (max-width: 767px) {
		width: 100%;
		height: 100px;
	}
`;

const WrapperItem = styled.div`
	flex: 1;
	background: #ffffff;
`;

const AircraftDetailItem = styled.div`
	display: flex;
	gap: 40px;

	@media (max-width: 767px) {
		flex-direction: column;
		gap: 4px;
	}
`;

const AircraftDetailItemInner = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const ContainerRequestFlight = styled.div``;

const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;

const CustomDivider = styled(Divider)`
	margin: 8px 0;
`;

const ButtonCol = styled(Col)`
	max-width: 180px;
	width: 100%;
`;
